/**
 * usePhoneViewport — true on a PHONE-sized viewport: below Tailwind `md`
 * (768px). The Production Records surfaces render a different component
 * tree there (plan-06-mobile.md): a dated card timeline, the photo-first
 * record wizard, item cards in place of the eleven-column tables. Desktop
 * and tablet (`>= md`) render exactly what they always did.
 *
 * Deliberately NOT `useIsMobile`: the `@ordo/ui` hook counts any touch
 * device as mobile (an iPad keeps the desktop layout here), and the `@/lib`
 * one is the `lg` breakpoint the school-orders rail hides at.
 *
 * Read SYNCHRONOUSLY in the state initializer (the pattern of
 * `hooks/use-coarse-pointer.ts`), so the first paint is already the phone
 * tree and a test can set `window.innerWidth` before mounting. Stays live
 * through the media query's `change` event (a rotation, DevTools emulation).
 */

import { useEffect, useState } from 'react'

export const PHONE_VIEWPORT_QUERY = '(max-width: 767.98px)'

function readPhoneViewport(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(PHONE_VIEWPORT_QUERY).matches
  )
}

export function usePhoneViewport(): boolean {
  const [phone, setPhone] = useState(readPhoneViewport)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const media = window.matchMedia(PHONE_VIEWPORT_QUERY)
    const update = () => setPhone(media.matches)
    // Anything that moved between the initializer and this subscription.
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return phone
}
